import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import packagingWarehouse from '../class/packagingWarehouse';
import { formatRpcError } from '../class/jrpc20WSClient';
import { genOperationId, toNum, fmtQty, perColloOf, piecesWholeFrom } from '../class/util';
import swal from 'sweetalert';
import PkgSelect from './component/PkgSelect';
import PositionPicker from './component/PositionPicker';
import QtyBlock from './component/QtyBlock';

// Rientro avanzi. L'operatore è il magazziniere loggato. Due percorsi:
//
//  - CON ORDINE (facoltativo, include gli EVASI perché una lavorazione conclusa genera eccedenze):
//    la divisione si ricava dall'ordine e posizione/lotto/pezzatura sono suggeriti da dove era stato
//    prelevato (modificabili). Il codice si scegli fra ciò che l'ordine ha prelevato OPPURE
//    dall'anagrafica: un avanzo può appartenere a un ordine su cui quell'articolo non risulta mai
//    uscito, e obbligare a togliere l'ordine farebbe perdere il collegamento del movimento. Quando
//    l'articolo non è fra i prelievi il netto non è confrontabile e il confronto si spegne.
//  - SENZA ORDINE: capita di non poter risalire a quale ordine sta rientrando il materiale. Qui la
//    divisione va scelta (fa parte della chiave di giacenza e non è derivabile) e il codice si
//    prende dall'anagrafica, perché non c'è un elenco di prelievi da cui partire. Niente
//    suggerimenti né confronto col netto prelevato: non esistono.
const RientroPage = () => {
    const navigate = useNavigate();
    const ws = new packagingWarehouse();

    const [orders, setOrders] = useState([]);
    const [positions, setPositions] = useState([]);
    const [orderUuid, setOrderUuid] = useState('');
    const [order, setOrder] = useState(null);
    const [summary, setSummary] = useState(null);   // righe prelevate (net_qty)
    const [sel, setSel] = useState(null);            // componente scelto + campi
    const [saving, setSaving] = useState(false);

    // Percorso SENZA ordine: capita di non poter risalire a quale ordine sta rientrando il
    // materiale. Lì la divisione non si può derivare dall'ordine e va scelta, e l'articolo si
    // prende dall'anagrafica (non dai prelievi dell'ordine, che non c'è).
    const [customers, setCustomers] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [components, setComponents] = useState([]);
    const [customerUuid, setCustomerUuid] = useState('');
    const [divisionUuid, setDivisionUuid] = useState('');

    useEffect(() => {
        // scope 'rientro': include anche gli ordini EVASI (eccedenze di lavorazioni concluse).
        Promise.all([ws.getOrders('rientro'), ws.getPositions(), ws.getDivisionCatalog(), ws.getComponents()])
            .then(([o, pos, cat, comp]) => {
                setOrders(o.orders || []);
                setPositions(pos.positions || []);
                setCustomers(cat.customers || []);
                setDivisions(cat.divisions || []);
                setComponents(comp.components || []);
            })
            .catch(err => swal('Errore', formatRpcError(err), 'error'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const divisionsForCustomer = divisions.filter(d => String(d.customer_uuid) === String(customerUuid));

    // La divisione fa parte della chiave di riga e il BE la esige. Dall'ordine si ricava solo se
    // l'ordine ce l'ha: passa da indirizzo di spedizione → divisione cliente, e un ordine senza
    // indirizzo non la ha. In quel caso va scelta a mano anche CON l'ordine selezionato — come fa il
    // gestionale — altrimenti l'unica via sarebbe togliere l'ordine e perdere il collegamento.
    const divisionFromOrder = !!(orderUuid && order && order.division_uuid);
    const needsManualDivision = !divisionFromOrder;
    // Divisione effettiva, per i suggerimenti dalla giacenza (che è segmentata per divisione).
    const effectiveDivision = divisionFromOrder ? order.division_uuid : divisionUuid;

    const cellaOf = (uuid) => { const p = positions.find(x => x.uuid === uuid); return p ? p.cella : ''; };

    const onOrderChange = (uuid) => {
        setOrderUuid(uuid); setSel(null); setSummary(null);
        // Cambiando percorso si azzerano i filtri dell'altro: divisione dall'ordine e divisione
        // scelta a mano sono la stessa informazione presa da due fonti, tenerle entrambe
        // significherebbe non sapere quale vince.
        setCustomerUuid(''); setDivisionUuid('');
        const o = orders.find(x => x.order_uuid === uuid) || null;
        setOrder(o);
        if (o) {
            // NON si filtra su net_qty > 0: un ordine già interamente rientrato mostrerebbe
            // "nessun articolo" pur avendo movimentazioni, e l'operatore non ha modo di capire
            // perché. Le righe si mostrano tutte, segnalando quelle a saldo esaurito.
            ws.getOrderPickSummary(uuid)
                .then(r => setSummary(r.rows || []))
                .catch(err => swal('Errore', formatRpcError(err), 'error'));
        }
    };

    // ── Cliente/divisione a mano ──
    // `setSel(null)` SOLO senza ordine: là l'articolo si scegli in questa stessa card, dopo la
    // divisione (il campo è disabilitato finché manca), quindi cambiando divisione l'articolo scelto
    // apparteneva a un'altra divisione e va rifatto. CON l'ordine, invece, questa card serve solo a
    // dare la divisione che l'ordine non ha: l'articolo viene dal riepilogo (o dalla sua tendina) e
    // azzerarlo butterebbe via posizione, lotto, colli, pezzatura, colli aperti e note già digitati —
    // come il gestionale, che al cambio cliente/divisione non svuota niente.
    const onCustomerChange = (v) => { setCustomerUuid(v); setDivisionUuid(''); if (!orderUuid) { setSel(null); } };
    const onDivisionChange = (v) => { setDivisionUuid(v); if (!orderUuid) { setSel(null); } };

    // Netto prelevato di un articolo scelto dall'anagrafica mentre un ordine è selezionato.
    // Se l'ordine quell'articolo l'ha prelevato (magari su più lotti) il confronto si può ancora
    // fare, sommando le sue righe; se non l'ha mai prelevato non c'è niente da confrontare → null,
    // e hint e avviso sull'eccedenza spariscono invece di parlare di un altro articolo.
    const netForComponent = (uuid) => {
        const rows = (summary || []).filter(r => String(r.component_uuid) === String(uuid));
        return rows.length ? rows.reduce((s, r) => s + (Number(r.net_qty) || 0), 0) : null;
    };

    // CON ORDINE, articolo scelto dall'anagrafica invece che dalla tabella dei prelievi: un avanzo
    // può appartenere a un ordine su cui quell'articolo non risulta essere mai uscito (prelievo
    // registrato su un altro ordine, materiale spostato fra lavorazioni, prelievo mai registrato).
    // Passa da pickComponent, così la catena dei suggerimenti resta una sola: prelievo dell'ordine
    // e, se non c'è, giacenza esistente.
    const onOrderComponent = (uuid) => {
        const comp = components.find(c => String(c.uuid) === String(uuid));
        if (!comp) { setSel(null); return; }
        const rows = (summary || []).filter(r => String(r.component_uuid) === String(uuid));
        pickComponent({
            component_uuid: comp.uuid,
            component_code: comp.code,
            // Il lotto si propone solo se non è ambiguo: con più lotti prelevati lo decide il
            // suggerimento (o l'operatore).
            lotto: rows.length === 1 ? rows[0].lotto : '',
            net_qty: netForComponent(uuid),
        });
    };

    // Scelto l'articolo si passa ai campi del rientro. Senza ordine non c'è un prelievo da cui
    // dedurre la posizione, ma c'è la GIACENZA ESISTENTE: se quell'articolo è già stoccato in
    // quella divisione, si propone quella cella (con lotto e pezzatura), così il materiale torna
    // dov'era invece di creare una riga nuova altrove.
    const onManualComponent = async (uuid) => {
        const comp = components.find(c => String(c.uuid) === String(uuid));
        if (!comp) { setSel(null); return; }
        const s = await suggestFromStock(divisionUuid, comp.code);
        setSel({
            component_uuid: comp.uuid,
            component_code: comp.code,
            net_qty: null,        // quantità prelevata non nota: nessun confronto possibile
            position_cella: s ? s.position_cella : '',
            lotto: s ? s.lotto : '',
            colliWhole: '', perColloWhole: s ? s.perColloWhole : '', opens: [],
            note: '',
            suggestedFrom: s ? 'giacenza' : null,
            otherPositions: s ? s.otherPositions : 0,
        });
    };

    // Suggerimento dalla GIACENZA ESISTENTE: dove quell'articolo è già stoccato in quella divisione.
    // Serve sia senza ordine (non c'è un prelievo da cui dedurre la posizione) sia con un ordine che
    // non ha prelievi registrati per quel codice. Si preferisce la riga INTERI: è quella su cui i
    // colli interi si sommano, e la sua pezzatura è il selettore della riga di giacenza.
    const suggestFromStock = async (division_uuid, code) => {
        if (!division_uuid || !code) return null;
        try {
            const r = await ws.getPositionsByCode(division_uuid, code);
            const rows = r.rows || [];
            if (!rows.length) return null;
            const pick = rows.find(x => !Number(x.is_partial)) || rows[0];
            const pc = perColloOf(pick);
            return {
                position_cella: cellaOf(pick.position_uuid),
                lotto: pick.lotto || '',
                perColloWhole: pc > 0 ? fmtQty(pc) : '',
                // Quante celle diverse ospitano già quel codice: se più di una il suggerimento è
                // una fra tante e l'operatore deve saperlo, non subirlo.
                otherPositions: new Set(rows.map(x => x.position_uuid)).size,
            };
        } catch (e) {
            return null;   // il suggerimento è un aiuto: se non arriva, i campi restano da compilare
        }
    };

    const pickComponent = async (row) => {
        // Suggerisce posizione+lotto+PEZZATURA da dove l'ordine ha prelevato quel componente.
        // La pezzatura fa parte della chiave di riga: senza, l'avanzo creerebbe una riga nuova
        // invece di tornare su quella da cui è stato prelevato.
        let position_cella = '';
        let lotto = row.lotto || '';
        let perColloWhole = '';
        let suggestedFrom = null;
        try {
            const loc = await ws.getOrderPickLocation(orderUuid, row.component_uuid);
            if (loc.location && loc.location.position_uuid) {
                position_cella = cellaOf(loc.location.position_uuid);
                if (loc.location.lotto) lotto = loc.location.lotto;
                const pc = perColloOf(loc.location);
                if (pc > 0) perColloWhole = fmtQty(pc);
                suggestedFrom = 'ordine';
            }
        } catch (e) { /* fallback sotto: giacenza esistente */ }

        // L'ordine può non avere prelievi registrati per quel codice: allora si ricade sulla
        // giacenza esistente, meglio che lasciare i campi vuoti.
        if (!position_cella) {
            const s = await suggestFromStock(effectiveDivision, row.component_code);
            if (s) {
                position_cella = s.position_cella;
                lotto = s.lotto || lotto;
                perColloWhole = s.perColloWhole;
                suggestedFrom = 'giacenza';
            }
        }

        setSel({
            component_uuid: row.component_uuid,
            component_code: row.component_code,
            net_qty: row.net_qty,
            position_cella, lotto,
            colliWhole: '', perColloWhole, opens: [],
            note: '',
            suggestedFrom,
        });
    };

    const upd = (k, v) => setSel(prev => ({ ...prev, [k]: v }));
    // Colli aperti: uno per click, ognuno col suo residuo (colli diversi hanno residui diversi).
    const addOpen = () => setSel(prev => ({ ...prev, opens: [...prev.opens, ''] }));
    const updOpen = (i, v) => setSel(prev => ({ ...prev, opens: prev.opens.map((x, idx) => idx === i ? v : x) }));
    const removeOpen = (i) => setSel(prev => ({ ...prev, opens: prev.opens.filter((_, idx) => idx !== i) }));

    // Avviso (non blocco): sta rientrando più di quanto l'ordine ha effettivamente prelevato.
    // È la domanda vera dell'operatore su questa schermata ("ho rimesso tutto quello che ho preso?")
    // e finora non aveva risposta.
    const rientroWarn = (() => {
        // Senza ordine il netto prelevato non esiste: non c'è nulla con cui confrontare.
        if (!sel || sel.net_qty === null || sel.net_qty === undefined) return null;
        const tot = piecesWholeFrom(sel.colliWhole, sel.perColloWhole) + sel.opens.reduce((sum, v) => sum + toNum(v), 0);
        const net = Number(sel.net_qty) || 0;
        // Netto <= 0: l'ordine ha già ricevuto indietro tutto (o più) di quanto aveva prelevato.
        // Prima questo caso era muto, quindi si registrava un'eccedenza senza accorgersene.
        if (net <= 0) {
            return `L'ordine risulta già interamente rientrato (netto ${fmtQty(net)} pz): quello che registri qui è materiale in eccesso rispetto a quanto prelevato.`;
        }
        return tot > net
            ? `Attenzione: stai rientrando ${fmtQty(tot)} pz ma l'ordine ne ha prelevati ${fmtQty(net)}.`
            : null;
    })();

    const handleSave = async () => {
        const position = positions.find(p => p.cella === sel.position_cella.trim());
        if (!position) { swal('Attenzione', 'Posizione non valida', 'warning'); return; }
        // La divisione fa parte della chiave di riga e il BE la esige. La deriva dall'ordine solo se
        // l'ordine ce l'ha: un ordine senza indirizzo di spedizione non la ha, e in quel caso va
        // scelta a mano qui (vedi needsManualDivision) invece di far fallire il salvataggio con
        // "Divisione, posizione e codice obbligatori", che a schermo non si capisce.
        if (!divisionFromOrder && !divisionUuid) { swal('Attenzione', 'Selezionare la divisione', 'warning'); return; }

        const cW = toNum(sel.colliWhole), perCollo = toNum(sel.perColloWhole);
        const pW = piecesWholeFrom(sel.colliWhole, sel.perColloWhole);
        const opens = sel.opens.map(v => ({ pieces: toNum(v) })).filter(o => o.pieces > 0);
        const pP = opens.reduce((sum, o) => sum + o.pieces, 0);
        if (cW > 0 && perCollo <= 0) { swal('Attenzione', 'Indicare i pezzi per collo', 'warning'); return; }
        if (perCollo > 0 && cW <= 0 && pP <= 0) { swal('Attenzione', 'Indicare il numero di colli interi', 'warning'); return; }
        if (pW + pP <= 0) { swal('Attenzione', 'Inserire la quantità da rientrare', 'warning'); return; }

        const confirm = await swal({
            title: 'Conferma Rientro',
            text: `Rientrare ${fmtQty(pW + pP)} pz di ${sel.component_code} in ${position.cella}?`,
            icon: 'info', buttons: ['Annulla', 'Conferma'],
        });
        if (!confirm) return;

        setSaving(true);
        try {
            await ws.returnLeftover({
                // L'ordine si manda sempre che ci sia (il movimento resta collegato). La divisione la
                // deriva il BE dall'ordine, ma solo se l'ordine ce l'ha: altrimenti la mandiamo noi,
                // e un division_uuid esplicito ha comunque la precedenza lato BE.
                ...(orderUuid ? { order_uuid: orderUuid } : {}),
                ...(divisionFromOrder ? {} : { division_uuid: divisionUuid }),
                component_uuid: sel.component_uuid,
                position_uuid: position.uuid,
                lotto: sel.lotto.trim(),
                qty_colli_whole: cW,
                // I pezzi interi li deriva il BE; la pezzatura seleziona la riga di giacenza.
                qty_per_collo_whole: perCollo,
                // Un elemento per collo aperto rientrato: ognuno diventa una riga col suo residuo.
                opens,
                note: sel.note.trim(),
                client_operation_id: genOperationId(),
            });
            await swal('Rientro effettuato', `${fmtQty(pW + pP)} pz rientrati in ${position.cella}.`, 'success');
            navigate('/home');
        } catch (err) {
            swal('Errore', formatRpcError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="pkg-page">
            <div className="pkg-page-header">
                <h2 className="pkg-title">Rientro Avanzi</h2>
            </div>

            <div className="pkg-card">
                <div className="pkg-row">
                    <div className="pkg-field">
                        {/* FACOLTATIVO: capita di non poter risalire all'ordine. Include anche gli
                            ordini EVASI, che generano rientri come eccedenze di lavorazioni concluse. */}
                        <label>Ordine <span className="muted">(facoltativo)</span></label>
                        <PkgSelect
                            value={orderUuid}
                            options={orders.map(o => ({ value: o.order_uuid, label: `${o.order_production_number} — ${o.article_code} (${o.client_name}) [${o.status_label}]` }))}
                            onChange={onOrderChange}
                            placeholder="Nessun ordine — rientro non riconducibile"
                            noOptionsText="Nessun ordine"
                        />
                        <span className="pkg-hint">Lascia vuoto se non sai da quale ordine rientra il materiale.</span>
                    </div>
                    {divisionFromOrder && (
                        <div className="pkg-field">
                            <label>Divisione</label>
                            <input className="pkg-readonly" readOnly value={order.division_name || ''} placeholder="—" />
                            <span className="pkg-hint">Ricavata dall'ordine</span>
                        </div>
                    )}
                </div>
            </div>

            {/* La divisione va scelta a mano in due casi: senza ordine (non c'è nulla da cui
                ricavarla) e con un ordine che non ne ha una. Nel primo caso serve anche l'articolo,
                perché non esiste un elenco di prelievi da cui partire; nel secondo l'articolo lo dà
                la tabella del riepilogo (o la sua tendina), quindi qui basta la divisione. */}
            {needsManualDivision && (
                <div className="pkg-card">
                    <div className="pkg-section-title">
                        {orderUuid ? "L'ordine non ha una divisione: indicala" : 'Rientro senza ordine'}
                    </div>
                    <div className="pkg-row">
                        <div className="pkg-field">
                            <label>Cliente</label>
                            <PkgSelect
                                value={customerUuid}
                                options={customers.map(c => ({ value: c.uuid, label: c.name }))}
                                onChange={onCustomerChange}
                                placeholder="Cerca cliente..."
                                noOptionsText="Nessun cliente"
                            />
                        </div>
                        <div className="pkg-field">
                            <label>Divisione</label>
                            <PkgSelect
                                value={divisionUuid}
                                options={divisionsForCustomer.map(d => ({ value: d.uuid, label: d.name }))}
                                onChange={onDivisionChange}
                                isDisabled={!customerUuid}
                                placeholder={customerUuid ? 'Cerca divisione...' : 'Seleziona prima il cliente'}
                                noOptionsText="Nessuna divisione"
                            />
                        </div>
                        {/* Con un ordine selezionato l'articolo lo dà la tabella del riepilogo (o la
                            sua tendina): qui servirebbe solo a offrire due campi articolo diversi. */}
                        {!orderUuid && (
                            <div className="pkg-field">
                                <label>Codice articolo</label>
                                <PkgSelect
                                    value={sel ? sel.component_uuid : ''}
                                    options={components.map(c => ({ value: c.uuid, label: `${c.code} - ${c.description}` }))}
                                    onChange={onManualComponent}
                                    isDisabled={!divisionUuid}
                                    placeholder={divisionUuid ? 'Cerca articolo...' : 'Seleziona prima la divisione'}
                                    noOptionsText="Nessun articolo"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {summary !== null && (
                <div className="pkg-card">
                    <div className="pkg-section-title">Materiale prelevato dall'ordine</div>
                    <div className="pkg-table-wrap">
                        <table>
                            <thead>
                                <tr className="table-head">
                                    <th>Codice</th><th>Descrizione</th><th>Lotto</th>
                                    <th className="text-right">Prelevato</th>
                                    <th className="text-right">Rientrato</th>
                                    <th className="text-right">Netto</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.map((row, i) => {
                                    // Netto <= 0 = già interamente rientrato. La riga resta VISIBILE e
                                    // azionabile (si può dover registrare altro materiale), ma è
                                    // marcata: nasconderla lasciava l'elenco vuoto senza spiegazione.
                                    const net = Number(row.net_qty) || 0;
                                    const done = net <= 0;
                                    return (
                                        <tr key={row.component_uuid + '-' + i} className={done ? 'pkg-row-empty' : 'order'}>
                                            <td>{row.component_code}</td>
                                            <td>{row.component_description}</td>
                                            <td>{row.lotto}</td>
                                            <td className="text-right muted">{fmtQty(row.picked_qty)}</td>
                                            <td className="text-right muted">{fmtQty(row.returned_qty)}</td>
                                            <td className="text-right">
                                                <strong>{fmtQty(net)}</strong>
                                                {done && <div className="muted pkg-cell-sub">già rientrato</div>}
                                            </td>
                                            <td><button className="btn-sm blue_butt" onClick={() => pickComponent(row)}>Rientra</button></td>
                                        </tr>
                                    );
                                })}
                                {summary.length === 0 && <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px' }} className="muted">Quest'ordine non ha prelievi di packaging registrati. Puoi registrare comunque il rientro scegliendo l'articolo qui sotto.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    {/* Un avanzo può appartenere a un ordine su cui quell'articolo non risulta mai
                        uscito: prelievo registrato su un altro ordine, materiale passato da una
                        lavorazione all'altra, o prelievo mai registrato. Senza questo campo l'unico
                        modo era togliere l'ordine, perdendo il collegamento del movimento. */}
                    <div className="pkg-row">
                        <div className="pkg-field">
                            <label>Codice articolo</label>
                            <PkgSelect
                                value={sel ? sel.component_uuid : ''}
                                options={components.map(c => ({ value: c.uuid, label: `${c.code} - ${c.description}` }))}
                                onChange={onOrderComponent}
                                placeholder="Cerca articolo..."
                                noOptionsText="Nessun articolo"
                            />
                            <span className="pkg-hint">
                                Serve anche per articoli che non compaiono fra i prelievi di quest'ordine:
                                il movimento resta comunque collegato all'ordine.
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {sel && (
                <div className="pkg-card">
                    <div className="pkg-section-title">Rientro — {sel.component_code}</div>
                    <div className="pkg-row">
                        <div className="pkg-field">
                            <label>Posizione (cella)</label>
                            <PositionPicker
                                positions={positions}
                                value={sel.position_cella}
                                onChange={v => upd('position_cella', v)}
                            />
                            {/* Da dove viene il suggerimento: dal prelievo dell'ordine, oppure dalla
                                giacenza già presente. Dirlo conta, perché sono affidabilità diverse:
                                il primo è dove quel materiale era davvero, il secondo è solo dove
                                quell'articolo è stoccato di solito. */}
                            <span className="pkg-hint">
                                {sel.suggestedFrom === 'ordine'
                                    ? "Suggerita da dove l'ordine ha prelevato"
                                    : sel.suggestedFrom === 'giacenza'
                                        ? `Suggerita dalla giacenza esistente${sel.otherPositions > 1 ? ` — l'articolo è in ${fmtQty(sel.otherPositions)} celle, verifica` : ''}`
                                        : 'Indica dove riporre il materiale'}
                            </span>
                        </div>
                    </div>
                    <div className="pkg-row">
                        <div className="pkg-field"><label>Lotto</label><input value={sel.lotto} onChange={e => upd('lotto', e.target.value)} placeholder="(nessun lotto)" /></div>
                    </div>
                    <QtyBlock
                        title="Quantità da rientrare"
                        colliWhole={sel.colliWhole}
                        perColloWhole={sel.perColloWhole}
                        opens={sel.opens}
                        onAddOpen={addOpen}
                        onChangeOpen={updOpen}
                        onRemoveOpen={removeOpen}
                        onChange={upd}
                        // Il netto prelevato manca in due casi diversi, e vanno distinti: senza ordine
                        // non c'è proprio nulla da confrontare (silenzio), con un ordine che non ha
                        // prelevato quell'articolo il silenzio si leggerebbe come "controllato, ok".
                        // La frase si dice solo se è VERIFICATA sul riepilogo attuale, non sullo
                        // snapshot in `sel`: scegliendo un articolo e poi un ordine mentre il
                        // suggerimento è ancora in volo, `sel.net_qty` resta null ma l'articolo può
                        // essere nella tabella qui sopra con un netto reale.
                        hint={sel.net_qty === null || sel.net_qty === undefined
                            ? ((orderUuid && summary !== null && netForComponent(sel.component_uuid) === null)
                                ? "Quest'ordine non ha prelievi registrati di questo articolo: nessun confronto possibile."
                                : null)
                            : `Netto prelevato dall'ordine: ${fmtQty(sel.net_qty)} pz`}
                        warn={rientroWarn}
                        bare
                    />
                    <div className="pkg-row">
                        <div className="pkg-field"><label>Note</label><textarea rows="2" value={sel.note} onChange={e => upd('note', e.target.value)} placeholder="Motivo / riferimento" /></div>
                    </div>
                    <div className="pkg-actions-right">
                        <button className="btn-indietro" onClick={() => setSel(null)}>Annulla</button>
                        <button className="blue_butt" onClick={handleSave} disabled={saving}>{saving ? 'Salvataggio...' : 'Conferma Rientro'}</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RientroPage;
